import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import { 
  getUserTeams, 
  getInvoicesForReport, 
  formatCurrency
} from '../services/firebaseService';
import { 
  Team, 
  Invoice
} from '../types';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import './MonthlyReport.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface MonthlyData {
  team: string;
  months: string[];
  values: number[];
  totalValue: number;
  averageValue: number;
  totalInvoices: number;
}

const MonthlyReport: React.FC = () => {
  const { user } = useAuth();
  const [teams, setTeams] = useState<Team[]>([]);
  const [selectedTeams, setSelectedTeams] = useState<string[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [monthlyGoal, setMonthlyGoal] = useState(124000);

  const loadUserTeams = async () => {
    try {
      const userTeams = await getUserTeams(user!.uid);
      setTeams(userTeams);
      setSelectedTeams([]);
    } catch (error) {
      console.error('Error loading teams:', error);
    }
  };

  useEffect(() => {
    if (user) {
      loadUserTeams();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const getMonthRange = (monthsBack: number) => {
    const currentDate = new Date();
    const currentDay = currentDate.getDate();
    
    console.log(`Current date: ${currentDate.toISOString().split('T')[0]}, day: ${currentDay}`);
    
    // Determine current billing cycle
    let currentBillingStart: Date;
    if (currentDay < 26) {
      // We're in a billing cycle that started last month (26th of last month to 25th of this month)
      currentBillingStart = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 26);
    } else {
      // We're in a billing cycle that started this month (26th of this month to 25th of next month)
      currentBillingStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), 26);
    }
    
    console.log(`Current billing cycle starts: ${currentBillingStart.toISOString().split('T')[0]}`);
    
    // Go back the specified number of billing cycles (months)
    // For 6 months: go back 6 billing cycles from current
    const oldestBillingStart = new Date(currentBillingStart.getFullYear(), currentBillingStart.getMonth() - (monthsBack - 1), 26);
    
    // End date should be current date to capture all available data
    const endDate = new Date();
    
    console.log(`Loading from oldest billing cycle: ${oldestBillingStart.toISOString().split('T')[0]} to ${endDate.toISOString().split('T')[0]}`);
    
    const startDateStr = `${oldestBillingStart.getFullYear()}-${String(oldestBillingStart.getMonth() + 1).padStart(2, '0')}-26`;
    const endDateStr = `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;
    
    return { startDate: startDateStr, endDate: endDateStr };
  };

  const loadReportData = async () => {
    if (selectedTeams.length === 0) {
      alert('Selecione pelo menos uma equipe.');
      return;
    }

    setLoading(true);
    try {
      const allInvoices: Invoice[] = [];
      
      // Load data for the last 12 months
      const { startDate, endDate } = getMonthRange(11);
      console.log(`Loading data for 12 months: ${startDate} to ${endDate}`);
      
      for (const teamId of selectedTeams) {
        const teamInvoices = await getInvoicesForReport(teamId, startDate, endDate);
        console.log(`Team ${teamId}: loaded ${teamInvoices.length} invoices`);
      
      // Debug: show date range of loaded invoices
      if (teamInvoices.length > 0) {
        const dates = teamInvoices.map(inv => inv.date || inv.header?.date).filter(d => d).sort();
        console.log(`${teamId} invoice date range:`, { first: dates[0], last: dates[dates.length - 1] });
      }
        allInvoices.push(...teamInvoices);
      }

      console.log(`Total invoices loaded: ${allInvoices.length}`);
      setInvoices(allInvoices);
    } catch (error) {
      console.error('Error loading report data:', error);
      alert('Erro ao carregar dados do relatório.');
    } finally {
      setLoading(false);
    }
  };


  const getDates = useCallback((sub: number) => {
    const baseDate = new Date();
    baseDate.setMonth(baseDate.getMonth() - sub);
    
    const lastDate = new Date(baseDate);
    const nextDate = new Date(baseDate);
    nextDate.setMonth(nextDate.getMonth() + 1);

    const initDate = `26/${String(lastDate.getMonth() + 1).padStart(2, '0')}/${lastDate.getFullYear()}`;
    const finalDate = `25/${String(nextDate.getMonth() + 1).padStart(2, '0')}/${nextDate.getFullYear()}`;

    return {
      initDate: initDateToTimestamp(initDate),
      finalDate: endDateToTimestamp(finalDate),
      initStrDate: initDate,
      finalStrDate: finalDate,
    };
  }, []);

  const initDateToTimestamp = (date: string): number => {
    const array = date.split("/");
    const dt = array[2] + "-" + array[1] + "-" + array[0] + " 00:00:00 GMT";
    return new Date(dt).getTime();
  };

  const endDateToTimestamp = (date: string): number => {
    const array = date.split("/");
    const dt = array[2] + "-" + array[1] + "-" + array[0] + " 23:59:00 GMT";
    return new Date(dt).getTime();
  };

  const checkBetweenData = useCallback((sub: number, check: number): boolean => {
    const dates = getDates(sub);
    const from = dates.initDate;
    const to = dates.finalDate;

    if (check <= to && check >= from) {
      return true;
    }
    return false;
  }, [getDates]);

  const getMonthKey = (monthsBack: number): string => {
    const monthNames = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 
                       'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];
    
    const currentDate = new Date();
    const targetDate = new Date(currentDate);
    targetDate.setMonth(targetDate.getMonth() - monthsBack + 1);
    
    return monthNames[targetDate.getMonth()];
  };

  const monthlyReportData = useMemo(() => {
    const teamData: { [key: string]: MonthlyData } = {};

    selectedTeams.forEach(teamId => {
      const teamName = teams.find(t => t.id === teamId)?.name || teamId;
      const teamInvoices = invoices.filter(inv => inv.teamId === teamId || inv.team === teamId);
      
      // Initialize data structure for 12 months
      const data = {
        dates: new Array(12).fill(''),
        values: new Array(12).fill(0),
        total: 0,
      };
      
      let totalInvoices = 0;

      teamInvoices.forEach(invoice => {
        // Only process invoices with sent_status === "true_opened" like the original
        if (invoice.sent_status === "true_opened") {
          const timeStamp = typeof invoice.date === 'number' ? invoice.date : 0;
          const totalValue = invoice.totalWithFactor || 0;
          
          if (timeStamp > 0) {
            totalInvoices++;
            
            // Check which month this invoice belongs to (1 = current month, 2 = 1 month back, etc.)
            for (let monthsBack = 1; monthsBack <= 12; monthsBack++) {
              if (checkBetweenData(monthsBack, timeStamp)) {
                const i = 12 - monthsBack; // Position in array (11 = current, 0 = 11 months back)
                data.values[i] += totalValue;
                break;
              }
            }
          }
        }
      });
      
      // Generate month labels for 12 months
      const months: string[] = [];
      for (let i = 0; i < 12; i++) {
        months.push(getMonthKey(11 - i));
      }
      
      const totalValue = data.values.reduce((sum, value) => sum + value, 0);
      const nonZeroValues = data.values.filter(v => v > 0);
      const averageValue = nonZeroValues.length > 0 ? totalValue / nonZeroValues.length : 0;

      teamData[teamId] = {
        team: teamName,
        months,
        values: data.values,
        totalValue,
        averageValue,
        totalInvoices
      };
    });

    return Object.values(teamData);
  }, [invoices, selectedTeams, teams, checkBetweenData]);

  const handleTeamToggle = (teamId: string) => {
    setSelectedTeams(prev => 
      prev.includes(teamId) 
        ? prev.filter(id => id !== teamId)
        : [...prev, teamId]
    );
  };

  const handleSelectAllTeams = () => {
    setSelectedTeams(teams.map(team => team.id));
  };

  const handleDeselectAllTeams = () => {
    setSelectedTeams([]);
  };

  const exportToCSV = () => {
    const headers = ['Equipe', ...monthlyReportData[0]?.months || [], 'Total', 'Média', 'Total Boletins'].join(';');

    const csvData = monthlyReportData.map(team => [
      `"${team.team}"`,
      ...team.values.map(v => v.toFixed(2)),
      team.totalValue.toFixed(2),
      team.averageValue.toFixed(2),
      team.totalInvoices.toString()
    ].join(';')).join('\n');

    const csvContent = [headers, csvData].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `relatorio-mensal-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const createChartData = (teamData: MonthlyData) => ({
    labels: teamData.months,
    datasets: [
      {
        label: 'Faturamento Mensal (R$)',
        data: teamData.values,
        backgroundColor: teamData.values.map(value => 
          value >= monthlyGoal ? 'rgba(56, 161, 105, 0.8)' : 'rgba(229, 62, 62, 0.8)'
        ),
        borderColor: teamData.values.map(value => 
          value >= monthlyGoal ? 'rgba(56, 161, 105, 1)' : 'rgba(229, 62, 62, 1)'
        ),
        borderWidth: 2,
        borderRadius: 4,
        borderSkipped: false,
      },
    ],
  });

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function(context: any) {
            const value = context.parsed.y;
            const isAboveGoal = value >= monthlyGoal;
            return [
              `Faturamento: R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
              `Meta: R$ ${monthlyGoal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
              `Status: ${isAboveGoal ? 'Acima da Meta' : 'Abaixo da Meta'}`
            ];
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        }
      },
      y: {
        ticks: {
          callback: function(value: any) {
            return 'R$ ' + value.toLocaleString('pt-BR');
          }
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.1)'
        }
      },
    },
  };

  return (
    <div className="monthly-report-container">
      <div className="report-header">
        <div className="header-content">
          <h1>Relatório Mensal</h1>
          <button onClick={() => window.close()} className="close-btn">
            <i className="fas fa-times"></i>
            Fechar
          </button>
        </div>
      </div>

      <div className="report-content">
        {/* Filters Section */}
        <div className="filters-section">
          <div className="filter-group">
            <div className="teams-header">
              <h3>Equipes</h3>
              <div className="team-controls">
                <button onClick={handleSelectAllTeams} className="control-btn">
                  Selecionar Todas
                </button>
                <button onClick={handleDeselectAllTeams} className="control-btn">
                  Desmarcar Todas
                </button>
              </div>
            </div>
            <div className="teams-grid">
              {teams.map((team) => (
                <label key={team.id} className="team-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedTeams.includes(team.id)}
                    onChange={() => handleTeamToggle(team.id)}
                  />
                  {team.name}
                </label>
              ))}
            </div>
          </div>

          <div className="filter-group">
            <h3>Configurações do Relatório</h3>
            <div className="config-inputs">
              <div className="config-input-group">
                <label htmlFor="monthlyGoal">Meta Mensal (R$):</label>
                <input
                  id="monthlyGoal"
                  type="number"
                  value={monthlyGoal}
                  onChange={(e) => setMonthlyGoal(Number(e.target.value) || 0)}
                  className="config-input"
                  min="0"
                  step="1000"
                  placeholder="124000"
                />
              </div>
            </div>
          </div>

          <div className="action-buttons">
            <button
              onClick={loadReportData}
              disabled={loading || selectedTeams.length === 0}
              className="load-btn"
            >
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  Carregando...
                </>
              ) : (
                <>
                  <i className="fas fa-chart-bar"></i>
                  Gerar Relatório
                </>
              )}
            </button>

            {monthlyReportData.length > 0 && (
              <button onClick={exportToCSV} className="export-btn">
                <i className="fas fa-download"></i>
                Exportar CSV
              </button>
            )}
          </div>
        </div>

        {/* Legend Section */}
        {monthlyReportData.length > 0 && (
          <div className="legend-section">
            <p>
              <span className="legend-item">
                <span className="legend-color above-goal"></span>
                Faturamento acima da meta mensal (R$ {monthlyGoal.toLocaleString('pt-BR')})
              </span>
              <span className="legend-item">
                <span className="legend-color below-goal"></span>
                Faturamento abaixo da meta mensal
              </span>
            </p>
          </div>
        )}

        {/* Charts Section */}
        {monthlyReportData.length > 0 && (
          <div className="charts-section">
            {monthlyReportData.map((teamData, index) => (
              <div key={teamData.team} className={`team-chart-card ${index % 3 === 0 && index > 0 ? 'page-break' : ''}`}>
                <div className="team-chart-header">
                  <h4>{teamData.team}</h4>
                  <div className="team-stats">
                    <span>
                      Faturamento médio: {
                        teamData.averageValue > 0 
                          ? formatCurrency(teamData.averageValue)
                          : 'Sem dados'
                      }
                    </span>
                    <br />
                    <small>
                      Total: {formatCurrency(teamData.totalValue)} | 
                      Boletins: {teamData.totalInvoices}
                    </small>
                  </div>
                </div>
                <div className="team-chart-container">
                  <Bar data={createChartData(teamData)} options={chartOptions} />
                </div>
              </div>
            ))}
          </div>
        )}

        {monthlyReportData.length === 0 && !loading && (
          <div className="no-data">
            <i className="fas fa-chart-line"></i>
            <h3>Nenhum dado carregado</h3>
            <p>Configure os filtros e clique em "Gerar Relatório" para visualizar o histórico de faturamento mensal das equipes.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MonthlyReport;
